import { MapPin, Trash2 } from 'lucide-react';
import { PricePerPieceTooltip } from '~/components/ui';
import { TransportLoadsBadge } from '~/components/ui/TransportLoadsBadge';
import { TransportSaveFailedBanner } from '~/components/ui/TransportSaveFailedBanner';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useRemoveCartItem, useUpdateCart } from '~/api';
import { useSubmitVehicleCapacity } from '~/hooks/useSubmitVehicleCapacity';
import {
  clearSellerDeliveryMethod,
  sellerDeliveryKey,
  setSellerDeliveryMethod,
} from '~/redux/reducers/cart';
import {
  vehicleTypeToTransportMode,
  useAutoSelectTransportVehicle,
  useTransportState,
  type TransportMode,
} from '~/hooks/useTransportState';
import {
  useCartTransportSave,
  useRetryTransportSaveWhen,
} from '~/hooks/useCartTransportSave';
import { useOptimisticCartQuantity } from '~/hooks/useOptimisticCartQuantity';
import { useItemsWithQuantityOverrides } from '~/hooks/useQuantityOverrides';
import { useDebouncedQuantityCommit } from '~/hooks/useDebouncedQuantityCommit';
import { wholeBundlePieces } from '~/components/ui/bundleQuantity';
import type {
  Facility,
  LogisticsIncoterm,
  Organization,
  Quantity,
  TransportVehicle,
} from '~/graphql/graphql';
import { usePreferredUnit } from '~/hooks/usePreferredUnit';
import {
  buildCartItemUpdateInput,
  calcItemLoads,
  calcSupportedVehicleIds,
  getEffectivePricePerPiece,
  piecesForQuantity,
  getVehiclePayload,
  getPlatformProductDimensions,
  type CartOrderItem,
  initialDeliveryMethod,
  type InheritedTransport,
  linesWithoutCapacityFor,
  productNamesOf,
} from '~/utils/order';
import { getPreferredOrFirstVehicle } from '~/utils/vehicleGroups';
import {
  cartLineTotal,
  extractGraphQLErrorMessages,
  formatCurrency,
  formatUnitPrice,
  sellerLabel,
  priceUnitLabel,
  roundTo6dp,
  type ClientError,
} from '~/utils';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput.tsx';
import { TransportCard } from '~/components/cards/TransportCard.tsx';
import { unitsPerPiece } from '~/utils/unitConversion';
import {
  facilityAddressCodeLabel,
  facilityAddressLabel,
} from '~/utils/facilityOptions';

export type CartProps = {
  organization: Organization;
  items: CartOrderItem[];
  sellerOrganizationFacility: Facility;
  shippingLocationFacility: Facility;
  shippingLocationId: string;
  id: string;
  parentOrderId?: string | null;
  incoTerm?: LogisticsIncoterm | null;
  transportVehicle?: Pick<TransportVehicle, 'id' | 'vehicleType'> | null;
  isInternational?: boolean;
  currency?: string | null;
  transportVehicleCatalog?: TransportVehicle[];
  isFromQuote?: boolean;
  inheritedTransport: InheritedTransport | null;
};

export function CartCard({
  organization,
  items: serverItems,
  sellerOrganizationFacility,
  shippingLocationFacility,
  shippingLocationId,
  id,
  parentOrderId,
  incoTerm: savedIncoTerm,
  transportVehicle: savedTransportVehicle,
  isInternational = false,
  currency,
  transportVehicleCatalog,
  isFromQuote = false,
  inheritedTransport,
}: Readonly<CartProps>) {
  const {
    failure: transportSaveFailure,
    save: saveTransport,
    retry: retryTransport,
    isSaving: isTransportSaving,
  } = useCartTransportSave(id);

  const {
    transport,
    deliveryMethod,
    selectedTransportVehicleId,
    handleTransportChange,
  } = useTransportState(saveTransport, {
    deliveryMethod: initialDeliveryMethod({
      isInternational,
      savedIncoTerm,
      hasSavedVehicle: !!savedTransportVehicle,
      inheritedIncoTerm: inheritedTransport
        ? inheritedTransport.incoTerm
        : null,
    }),
    selectedTransportVehicleId: savedTransportVehicle?.id,
    transport: savedTransportVehicle
      ? vehicleTypeToTransportMode(savedTransportVehicle.vehicleType)
      : undefined,
  });

  const dispatch = useDispatch();
  const {
    applyQuantityOverride,
    settleQuantityOverride,
    clearQuantityOverride,
  } = useOptimisticCartQuantity();
  const items = useItemsWithQuantityOverrides(id, serverItems);
  const sellerKey = sellerDeliveryKey(
    organization.id,
    sellerOrganizationFacility.id
  );
  useEffect(() => {
    dispatch(
      setSellerDeliveryMethod({ key: sellerKey, method: deliveryMethod })
    );
    return () => {
      dispatch(clearSellerDeliveryMethod(sellerKey));
    };
  }, [dispatch, sellerKey, deliveryMethod]);

  const handleQuantityOverride = (
    productId: string,
    value: number,
    piecesCount?: number
  ) => {
    if (!productId) return;
    applyQuantityOverride(id, productId, value, piecesCount);
  };

  const handleQuantityRevert = (productId: string) => {
    if (!productId) return;
    clearQuantityOverride(id, productId);
  };

  const handleQuantitySettled = (productId: string) => {
    if (!productId) return;
    settleQuantityOverride(id, productId);
  };

  const totalLoads = items.reduce((acc, item) => {
    const vehiclePayload = getVehiclePayload(
      item.platformProductsLengths?.platformProductVehicleCapacities,
      selectedTransportVehicleId
    );
    return acc + calcItemLoads(item, vehiclePayload);
  }, 0);

  const selectedVehicle = transportVehicleCatalog?.find(
    (v) => v.id === selectedTransportVehicleId
  );

  const { t } = useTranslation(['order-builder', 'cart']);

  const productVehicleIds = calcSupportedVehicleIds(items);

  const retryTransportSave = () =>
    retryTransport(deliveryMethod, selectedTransportVehicleId);

  const failedVehicleNowHasCapacity =
    transportSaveFailure.kind === 'missing-capacity' &&
    linesWithoutCapacityFor(items, selectedTransportVehicleId).length === 0;
  useRetryTransportSaveWhen(failedVehicleNowHasCapacity, retryTransportSave);

  // A missing capacity can be added inline, so the dropdown offers the full catalog;
  // international carts auto-select among containers only.
  const autoSelectCandidates = isInternational
    ? transportVehicleCatalog?.filter(
        (vehicle) => vehicle.vehicleType === 'INTERNATIONAL_CONTAINER'
      )
    : transportVehicleCatalog;

  // A cart with no saved vehicle starts on another cart's vehicle, so a capacity
  // refusal is explained in this card instead of a different vehicle being picked.
  const inheritedVehicle = inheritedTransport
    ? autoSelectCandidates?.find(
        (vehicle) =>
          vehicle.id === inheritedTransport.transportVehicleId &&
          (isInternational || vehicle.vehicleType !== 'INTERNATIONAL_CONTAINER')
      )
    : undefined;
  const firstAvailableVehicle =
    inheritedVehicle ??
    getPreferredOrFirstVehicle(autoSelectCandidates, productVehicleIds);
  // Empty carts have nothing to ship, so don't let the auto-select fall back to
  // the first facility vehicle and fire an unnecessary updateCartTransport.
  const availableVehicles =
    items.length > 0 && firstAvailableVehicle
      ? [firstAvailableVehicle]
      : undefined;

  useAutoSelectTransportVehicle(
    availableVehicles,
    selectedTransportVehicleId,
    handleTransportChange,
    deliveryMethod
  );

  return (
    <div className="bg-neut-light border border-neut-border rounded-lg overflow-hidden">
      <div className="bg-neut-50 border-b border-neut-border ">
        <div className="flex-1 px-3 pt-2 ">
          <p className="text-xs font-semibold text-neut-900">
            {sellerLabel(organization)}
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            <MapPin size={10} className="text-neut-400" aria-hidden="true" />
            <p className="text-xs text-neut-500">
              {facilityAddressCodeLabel(sellerOrganizationFacility)}
            </p>
          </div>
          {shippingLocationFacility && (
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={10} className="text-neut-400" aria-hidden="true" />
              <p className="text-xs text-neut-500">
                {facilityAddressLabel(shippingLocationFacility)}
              </p>
            </div>
          )}
          {selectedTransportVehicleId &&
            !isTransportSaving &&
            transportSaveFailure.kind === 'none' && (
              <div className="flex items-center gap-2 mt-1.5">
                <TransportLoadsBadge
                  transport={transport}
                  loads={totalLoads}
                  vehicleName={selectedVehicle?.name}
                  testId={
                    {
                      truck: 'cart-trucks',
                      railcar: 'cart-railcars',
                      container: 'cart-containers',
                    }[transport]
                  }
                />
              </div>
            )}
          <TransportSaveFailedBanner
            failure={transportSaveFailure}
            onRetry={retryTransportSave}
            productsNeedingCapacity={productNamesOf(
              linesWithoutCapacityFor(items, selectedTransportVehicleId)
            )}
            isSaving={isTransportSaving}
          />
          <TransportCard
            sellerOrganizationFacility={sellerOrganizationFacility}
            shippingLocationFacility={shippingLocationFacility}
            deliveryMethod={deliveryMethod}
            selectedTransportVehicleId={selectedTransportVehicleId}
            transportVehicleCatalog={transportVehicleCatalog}
            onTransportChange={handleTransportChange}
            isInternational={isInternational}
          />
        </div>
      </div>
      <div className="divide-y divide-neut-100">
        {[...items]
          .sort((a, b) => {
            const nameA =
              a.platformProductsLengths?.platformProduct?.name ?? '';
            const nameB =
              b.platformProductsLengths?.platformProduct?.name ?? '';
            if (nameA !== nameB) return nameA.localeCompare(nameB);
            const lenA =
              a.platformProductsLengths?.length?.actualLengthInches ?? 0;
            const lenB =
              b.platformProductsLengths?.length?.actualLengthInches ?? 0;
            return lenA - lenB;
          })
          .map((item) => {
            return (
              <CartItem
                key={item.platformProductsLengths?.id}
                length={item.platformProductsLengths?.lengthDisplayValue || ''}
                name={item.platformProductsLengths?.platformProduct?.name || ''}
                pricePerUnit={
                  (item.suggestedPrice ?? item.price)?.amount ?? null
                }
                quantity={item.quantity}
                productId={item.platformProductsLengths?.id || ''}
                cartId={id}
                facilityId={sellerOrganizationFacility.id}
                locationId={shippingLocationId}
                parentOrderId={parentOrderId}
                onQuantityChange={handleQuantityOverride}
                onQuantityRevert={handleQuantityRevert}
                onQuantitySettled={handleQuantitySettled}
                item={item}
                t={t}
                selectedTransportVehicleId={selectedTransportVehicleId}
                transport={transport}
                currency={currency}
                isFromQuote={isFromQuote}
              />
            );
          })}
      </div>
    </div>
  );
}

type CartItemProps = {
  length: string;
  name: string;
  pricePerUnit: number | null;
  quantity: Quantity;
  productId: string;
  cartId: string;
  facilityId: string;
  locationId: string;
  parentOrderId?: string | null;
  onQuantityChange: (
    productId: string,
    value: number,
    piecesCount?: number
  ) => void;
  onQuantityRevert: (productId: string) => void;
  onQuantitySettled: (productId: string) => void;
  item: CartOrderItem;
  t: (key: string) => string;
  selectedTransportVehicleId: string;
  transport: TransportMode;
  currency?: string | null;
  isFromQuote: boolean;
};

function CartItem({
  length,
  name,
  pricePerUnit,
  quantity,
  productId,
  cartId,
  facilityId,
  locationId,
  parentOrderId,
  onQuantityChange,
  onQuantityRevert,
  onQuantitySettled,
  item,
  t,
  selectedTransportVehicleId,
  transport,
  currency: cartCurrency,
  isFromQuote,
}: Readonly<CartItemProps>) {
  const [isDeleting, setIsDeleting] = useState(false);

  const currency = cartCurrency ?? undefined;
  const category = item.platformProductsLengths?.platformProduct?.category;
  const displayUnit = usePreferredUnit(category, quantity.unit);
  const { widthInches, thicknessInches, lengthInches } =
    getPlatformProductDimensions(
      item.platformProductsLengths?.platformProduct,
      item.platformProductsLengths?.length,
      item.platformProductsLengths?.isNominal
    );
  // quantity.unit is USER_PREFERRED as returned by the backend. displayUnit may
  // briefly differ during a preference switch while the query re-fetches.
  // Always compute piece count in quantity.unit (the actual unit of quantity.value)
  // so the conversion is never mismatched during that transient window.
  const displayPricePerUnit = pricePerUnit;
  const displayQuantity = quantity.value;
  const hasDims = lengthInches > 0 && widthInches > 0 && thicknessInches > 0;
  // Both come from the prop, which the parent's override already moves for the
  // quantity and the piece count together. Mirroring them locally used to be
  // needed when the override carried only the quantity, and left the total
  // stranded at a rejected value once a resync was skipped.
  const piecesCount: number = item.quantityStored?.value ?? 0;
  let uppDisplay: number | null = null;
  let rawPricePerPiece: number | null = null;
  if (hasDims || quantity.unit === 'PCS') {
    try {
      const upp = unitsPerPiece(
        quantity.unit,
        lengthInches,
        widthInches,
        thicknessInches
      );
      if (upp > 0) {
        uppDisplay = upp;
        const effective = getEffectivePricePerPiece(item);
        if (effective != null) {
          rawPricePerPiece = effective.amount;
        } else if (displayPricePerUnit != null) {
          rawPricePerPiece = displayPricePerUnit * upp;
        }
      }
    } catch {
      // unsupported unit
    }
  }
  const pricePerPiece =
    rawPricePerPiece != null ? roundTo6dp(rawPricePerPiece) : null;

  const piecesPerBundle = item.effectivePiecesPerBundle;
  // Size of one bundle in the item's own unit — lets handleQuantityChange
  // recover the exact bundle count first (see computeExactPieces) instead of
  // dividing straight through by unitsPerPiece, which drifts under CEILING
  // rounding on the backend.
  const bundleSizeInItemUnit = (() => {
    if (!piecesPerBundle || !hasDims) return 0;
    try {
      return (
        piecesPerBundle *
        unitsPerPiece(quantity.unit, lengthInches, widthInches, thicknessInches)
      );
    } catch {
      return 0;
    }
  })();

  const { mutate: updateCart } = useUpdateCart();
  const { mutate: removeCartItem } = useRemoveCartItem();
  const submitVehicleCapacity = useSubmitVehicleCapacity();

  const {
    trigger: triggerQuantityCommit,
    cancelPending: cancelPendingQuantityCommit,
    replacePending: replacePendingQuantityCommit,
  } = useDebouncedQuantityCommit<number>(
    (newValue) =>
      new Promise<void>((resolve, reject) => {
        updateCart(
          buildCartItemUpdateInput({
            productId,
            locationId,
            facilityId,
            quantity: newValue,
            unit: quantity.unit,
            parentOrderId,
          }),
          {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          }
        );
      }),
    (_newValue, outcome, isStale, error) => {
      if (isStale) {
        return;
      }
      setIsDeleting(false);
      if (outcome === 'error') {
        onQuantityRevert(productId);
        toast.error(
          `${t('order_builder.cart_item.cart_update_error')} ${extractGraphQLErrorMessages(
            error as ClientError
          ).join(', ')}`
        );
      } else {
        onQuantitySettled(productId);
        toast.success(t('order_builder.cart_item.cart_update_success'));
      }
    },
    3000
  );

  const handlePiecesPerBundleChange = (
    newPiecesPerBundle: number,
    quantityKeepingBundleCount: Quantity
  ) => {
    const sendQuantity = wholeBundlePieces(
      quantityKeepingBundleCount,
      newPiecesPerBundle,
      item.platformProductsLengths
    );
    void replacePendingQuantityCommit(
      (isStale) =>
        new Promise<void>((resolve) => {
          updateCart(
            buildCartItemUpdateInput({
              productId,
              locationId,
              facilityId,
              quantity: sendQuantity.value,
              unit: sendQuantity.unit,
              parentOrderId,
              piecesPerBundle: newPiecesPerBundle,
            }),
            {
              onSuccess: () => {
                if (!isStale()) {
                  onQuantitySettled(productId);
                }
                resolve();
              },
              onError: (error) => {
                if (!isStale()) {
                  onQuantityRevert(productId);
                }
                toast.error(
                  `${t('order_builder.cart_item.cart_update_error')} ${extractGraphQLErrorMessages(
                    error
                  ).join(', ')}`
                );
                resolve();
              },
            }
          );
        })
    );
  };

  const handleQuantityChange = (newValue: number) => {
    const piecesCount = piecesForQuantity(
      newValue,
      piecesPerBundle,
      bundleSizeInItemUnit,
      uppDisplay
    );
    onQuantityChange(productId, newValue, piecesCount);
    triggerQuantityCommit(newValue);
  };

  const handleDelete = () => {
    cancelPendingQuantityCommit();
    onQuantityRevert(productId);
    setIsDeleting(true);
    removeCartItem(
      { cartId, platformProductLengthId: productId },
      {
        onSuccess: () =>
          toast.success(t('order_builder.cart_item.cart_update_success')),
        onError: (error) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
        onSettled: () => setIsDeleting(false),
      }
    );
  };

  const handleCapacitySubmit = (value: number) => {
    if (!productId) return;
    submitVehicleCapacity({
      platformProductLengthId: productId,
      transportVehicleId: selectedTransportVehicleId,
      value,
      unit: quantity.unit,
    });
  };

  return (
    <div className="p-3">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="mb-1">
            <span className="inline-flex items-center px-2 py-0.5 bg-success-50 text-success-700 rounded text-xs font-medium">
              {length}
            </span>
          </div>
          <p data-testid="cart-item-name" className="text-xs text-neut-700">
            {name}
          </p>
        </div>
        <button
          type="button"
          data-testid="cart-item-delete-button"
          onClick={handleDelete}
          className="p-1 hover:bg-neut-100 rounded transition-colors ml-2 flex gap-2 cursor-pointer"
          disabled={isDeleting}
        >
          <Trash2
            className={`w-3.5 h-3.5 ${isDeleting ? 'text-error-600' : 'text-neut-400 '} hover:text-error-600`}
            aria-hidden="true"
          />
          {isDeleting && (
            <span
              data-testid="cart-item-deleting"
              className="text-xs text-error-600 ml-1"
            >
              {t('order_builder.cart_item.deleting')}
            </span>
          )}
        </button>
      </div>
      <div className="flex items-start justify-between gap-4">
        <fieldset disabled={isFromQuote} className="min-w-0 flex-1">
          <BundleQuantityInput
            quantity={item.quantity}
            platformProductsLength={item.platformProductsLengths}
            disabled={isDeleting}
            onBundleChange={(bundleCount) => {
              handleQuantityChange(bundleCount.value);
            }}
            vehiclePayload={getVehiclePayload(
              item.platformProductsLengths?.platformProductVehicleCapacities,
              selectedTransportVehicleId
            )}
            vehicleLabel={transport}
            onCapacitySubmit={handleCapacitySubmit}
            piecesPerBundle={item.effectivePiecesPerBundle}
            onPiecesPerBundleChange={handlePiecesPerBundleChange}
          />
        </fieldset>
        <div className="shrink-0 text-right flex flex-col items-end gap-1">
          <p
            data-testid={`cart-price-per-unit`}
            className="flex items-center gap-1 text-xs text-neut-500"
          >
            {displayPricePerUnit != null
              ? `${formatUnitPrice(displayPricePerUnit, currency)}/${priceUnitLabel(displayUnit)}`
              : t('cart:cart.price_not_available')}
            {pricePerPiece != null && displayUnit === 'PCS' && (
              <PricePerPieceTooltip
                pricePerPiece={pricePerPiece}
                currency={currency}
                ns="cart"
                iconOnly
                className="text-neut-500"
              />
            )}
          </p>
          {pricePerPiece != null && displayUnit !== 'PCS' && (
            <PricePerPieceTooltip
              pricePerPiece={pricePerPiece}
              currency={currency}
              ns="cart"
              className="text-xs font-semibold text-neut-500"
            />
          )}
          <p
            data-testid={`cart-total-price`}
            className="text-sm font-semibold text-neut-900"
          >
            {displayPricePerUnit != null
              ? formatCurrency(
                  cartLineTotal({
                    displayPricePerUnit,
                    rawPricePerPiece,
                    piecesCount,
                    displayQuantity,
                  }),
                  currency
                )
              : t('cart:cart.price_not_available')}
          </p>
        </div>
      </div>
    </div>
  );
}
